import {
  useState,
  ChangeEventHandler,
  MouseEventHandler,
  useEffect,
  useRef,
} from "react";
import { styled } from "@stitches/react";
import {
  Input,
  Button,
  SelectionList,
  RemovableTag,
  SelectionListStructuredValue,
} from "@/vendor/Forge/dist";
import SearchIcon from "@/components/Icons/search";
import { Filter, Filters } from "@/types/order-table";
import { Metrics } from "@/contexts/metrics-context";
import { Orders } from "@/contexts/orders-context";
import { useLocation } from "react-router-dom";
import { useFiltersWithSearchString } from "@/hooks/use-filters-search-string";
import { useDebounce } from "@/hooks/use-debounce";
import Loader from "@components/Loader";
import Warning from "@/assets/warning";
import { filterCap } from "@/utils/helpers";

const Wrapper = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: 16,
});

const Content = styled("div", {
  display: "flex",
  minHeight: "254px",
});

const CTA = styled("div", {
  display: "flex",
  flexDirection: "row-reverse",
});

const LeftPanel = styled("div", {
  width: "50%",
  borderRight: "1px solid  #B6B7B7",
  maxHeight: 320,
  overflowY: "scroll",
});

const Search = styled("div", {
  display: "flex",
  gap: 16,
  marginBottom: 16,

  "& svg": {
    position: "absolute",
    top: 8,
    left: 8,
  },
  "& > button": {
    width: 160,
  },
});

const RightPanel = styled("div", {
  width: "50%",
  paddingLeft: 8,
  maxHeight: 320,
  overflowY: "scroll",
});

const Summary = styled("div", {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: 16,

  "& > span": {
    fontWeight: "bold",
  },
});

const Tags = styled("div", {
  display: "flex",
  gap: 8,
  flexWrap: "wrap",
});

const setAllValuesTo = (
  originalList: string[],
  value: boolean,
  prev?: SelectionListStructuredValue,
): SelectionListStructuredValue => {
  const reduced = originalList?.reduce((acc, curr) => {
    return { ...acc, [curr]: value };
  }, {});
  return { ...prev, ...reduced };
};

const filtersMap: {
  [key: string]: Filter;
} = {
  insurance: "insurances",
  provider: "providerNames",
  department: "departments",
  authStatus: "authStatus",
  orderName: "orderNames",
  patientName: "patientNames",
  assignedUser: "assignedUser",
  clinicalFacilityDetails: "clinicalFacilityDetails",
  assignedUsers: "assignedUsers",
  secondaryInsurance: "secondaryInsurances",
  status: "statusList",
};

export const setInitialMasterList = (
  originalList: string[],
  type: Filter,
  activeFilters: Filters,
) => {
  const activeFiltersAsSet = new Set([
    ...(activeFilters?.[filtersMap[type]] ?? []),
  ]);
  if (!activeFiltersAsSet.size) return setAllValuesTo(originalList, false);
  return originalList.reduce(
    (acc, curr) => ({ ...acc, [curr]: activeFiltersAsSet.has(curr) }),
    {},
  );
};

const getSelectedList = (masterList: SelectionListStructuredValue) => {
  return Object.keys(masterList).filter((key) => masterList[key]);
};

const convertListToSelectionListStructuredValue = (
  masterList: SelectionListStructuredValue,
) => {
  return getSelectedList(masterList).reduce(
    (acc, curr) => ({ ...acc, [curr]: true }),
    {},
  );
};

interface Props {
  type: Filter;
  useContext: () => Metrics | Orders;
}

const AdvanceFilter = ({ type, useContext }: Props) => {
  const { filters: activeFilters, updateFilters } = useContext();

  const [searchKey, setSearchKey] = useState("");
  const [masterList, setMasterList] = useState<SelectionListStructuredValue>(
    setInitialMasterList([], type, activeFilters),
  );
  const [filteredList, setFilteredList] = useState<string[]>([]);
  const [selectedItemsPersistent, setSelectedItemsPersistent] = useState<
    string[]
  >([]);

  const selectedItems = getSelectedList(masterList);

  const { pathname } = useLocation();
  const debouncedInput = useDebounce(searchKey, 900);
  const {
    data: filterSuggestions,
    isError,
    error,
    isFetched,
    isFetching,
  } = useFiltersWithSearchString(type, debouncedInput);
  // Update filteredList whenever new suggestions come in
  const inputRef = useRef<HTMLInputElement | null>(null);
  /*eslint-disable react-hooks/exhaustive-deps */

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    setSelectedItemsPersistent(
      (activeFilters as Record<string, string[]>)?.[filtersMap[type]],
    );
  }, [activeFilters, type]);

  useEffect(() => {
    if (filterSuggestions && filterSuggestions.length > 0) {
      setFilteredList(filterSuggestions);
      setMasterList(
        setInitialMasterList(
          (activeFilters as Record<string, string[]>)?.[filtersMap[type]],
          type,
          activeFilters,
        ),
      );
    } else if (!filterSuggestions && selectedItems.length == 0) {
      setMasterList(setInitialMasterList([], type, activeFilters));
      setFilteredList([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSuggestions, activeFilters, type]);

  useEffect(() => {
    setMasterList({
      ...setAllValuesTo(filterSuggestions ?? [], false),
      ...setAllValuesTo(selectedItemsPersistent, true),
    });
    if (searchKey.length > 0 && filterSuggestions) {
      setFilteredList(
        filterSuggestions.filter((l) =>
          l.toLowerCase().includes(searchKey.toLocaleLowerCase()),
        ),
      );
    } else {
      setFilteredList(filterSuggestions ?? []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSuggestions, selectedItemsPersistent]);

  const filterExampleMap: Record<string, string> = {
    orderName: `"MRI"`,
    patientName: `"John Smith" or "Lisa Lee"`,
    insurance: `"BCBS" or "Aetna"`,
    secondaryInsurance: `"BCBS" or "Aetna"`,
    clinicalFacilityDetails: `"Seton Main"`,
    provider: `"Lisa Lee MD"`,
    departments: `"Primary_Main"`,
    department: `"Primary_Main"`,
    assignedUser: `"mbell48"`,
    authStatus: `"Not Started"`,
    priority: `"High"`,
  };

  const searchVerbiageMap: Record<string, string> = {
    orderName: `Order Name`,
    patientName: "Patient",
    insurance: `Primary Insurance`,
    secondaryInsurance: `Secondary Insurance`,
    clinicalFacilityDetails: `Rad Facility`,
    provider: "Order Provider",
    assignedUser: `Athena UserName`,
    department: `Department Name`,
  };

  const handleItemClick = (
    _:
      | React.MouseEvent<HTMLUListElement, MouseEvent>
      | React.KeyboardEvent<HTMLUListElement>,
    key: string | null,
  ) => {
    if (key === null) return;
    const isSelected = masterList[key];
    setMasterList({
      ...masterList,
      [key]: isSelected,
    });
    if (isSelected) {
      setSelectedItemsPersistent(selectedItems.filter((item) => item !== key));
      return;
    }
    setSelectedItemsPersistent([...selectedItems, key]);
  };

  const handleClearAllClick = () => {
    setMasterList(setAllValuesTo(filteredList, false));
    setSelectedItemsPersistent([]);
  };
  const handleSelectAllClick = () => {
    setMasterList(setAllValuesTo(filterSuggestions ?? [], true));
    setSelectedItemsPersistent([
      ...(selectedItemsPersistent ?? []),
      ...(filterSuggestions ?? []).filter(
        (item) => !selectedItemsPersistent?.includes(item),
      ),
    ]);
  };

  const handleSearch: ChangeEventHandler<HTMLInputElement> = (e) => {
    const key = e.target.value;
    setSearchKey(key);
    if (filteredList) setFilteredList(filteredList);
  };

  const handleRemoveTag = (key: string) => {
    const isSelected = masterList[key];
    setMasterList({
      ...masterList,
      [key]: isSelected,
    });
    if (isSelected) {
      setSelectedItemsPersistent(selectedItems.filter((item) => item !== key));
    }
  };
  const processFilters = (filtersMap: Filter, selectedItems: string[]) => {
    updateFilters(filtersMap, selectedItems);
  };
  const handleFilterResultsClick: MouseEventHandler<HTMLButtonElement> = () => {
    if (pathname.includes("user-activity")) {
      processFilters(filtersMap["assignedUsers"], selectedItems);
      const closeButton = document.querySelector(
        'button[aria-label="Close Modal"]',
      );
      if (closeButton) closeButton.id = "closeModal";
      const modalClose = document.getElementById("closeModal");
      if (modalClose) modalClose.click();
    } else {
      processFilters(filtersMap[type], selectedItems);
      const closeButton = document.querySelector(
        'button[aria-label="Close Modal"]',
      );
      if (closeButton) closeButton.id = "closeModal";
      const modalClose = document.getElementById("closeModal");
      if (modalClose) modalClose.click();
    }
  };

  const getIndefiniteArticle = (word: string) => {
    if (!word) return "a"; // fallback
    const vowels = ["a", "e", "i", "o", "u"];
    return vowels.includes(word[0].toLowerCase()) ? "an" : "a";
  };
  const warningMessage = `Please limit your selection to 500 or fewer ${searchVerbiageMap[type]}s`;

  return (
    <Wrapper>
      <Content>
        <LeftPanel>
          <Search>
            <Input
              placeholder="Search"
              icon={SearchIcon}
              onChange={handleSearch}
              value={searchKey}
              ref={inputRef}
            />
            <Button
              disabled={filteredList?.length ? false : true}
              variant="tertiary"
              onClick={handleSelectAllClick}
            >
              Select All
            </Button>
          </Search>
          <Loader
            isLoading={isFetching}
            displayText="Checking Available Filter Options"
            isError={isError}
            errorReason={error?.message}
          >
            {!isFetching && !isFetched && (
              <div>
                <p
                  style={{
                    fontSize: 20,
                    textAlign: "center",
                    marginBottom: "2px",
                    fontWeight: "490",
                  }}
                >
                  Search for {getIndefiniteArticle(searchVerbiageMap[type])}{" "}
                  {searchVerbiageMap[type]}{" "}
                </p>
                <p style={{ fontSize: 16, textAlign: "center" }}>
                  (such as: {filterExampleMap[type] ?? "example"})
                </p>
              </div>
            )}

            {debouncedInput && isFetched && filteredList?.length === 0 && (
              <p>No results match the search</p>
            )}
            {selectedItems?.length > filterCap && (
              <div style={{ display: "flex", flexDirection: "row" }}>
                <Warning
                  style={{
                    width: "24px",
                    height: "24px",
                    marginRight: "8px",
                    flexShrink: 0,
                  }}
                />
                <p>{warningMessage}</p>
              </div>
            )}

            {filteredList?.length > 0 && (
              <SelectionList
                options={filteredList}
                onClickItem={handleItemClick}
                value={convertListToSelectionListStructuredValue(masterList)}
              />
            )}
          </Loader>
        </LeftPanel>
        <RightPanel>
          <Summary>
            <span>Selected({selectedItemsPersistent?.length ?? 0})</span>

            <Button
              disabled={
                filteredList.length || selectedItemsPersistent?.length
                  ? false
                  : true
              }
              variant="tertiary"
              onClick={handleClearAllClick}
            >
              Clear All
            </Button>
          </Summary>
          <Tags>
            {selectedItemsPersistent?.map((selectedItem) => (
              <RemovableTag
                key={selectedItem}
                text={selectedItem}
                onRemove={() => handleRemoveTag(selectedItem)}
              />
            ))}
          </Tags>
        </RightPanel>
      </Content>
      <CTA>
        <Button
          disabled={selectedItemsPersistent?.length <= filterCap ? false : true}
          onClick={handleFilterResultsClick}
        >
          Filter Results
        </Button>
      </CTA>
    </Wrapper>
  );
};

export default AdvanceFilter;
